import {
    BOOKING_ENDED_EVENT,
    BOOKING_STARTED_EVENT,
    CONTACT_ACTIVITY_TYPE,
    PAYMENT_COMPLETED_EVENT,
} from '@constants/contacts';
import { AccIntegrationName } from '@constants/integrations';

export const automationsConstants = {
    AUTOMATIONS_FETCH: 'AUTOMATIONS_FETCH',
    AUTOMATIONS_SUCCESS: 'AUTOMATIONS_SUCCESS',
    AUTOMATIONS_ERROR: 'AUTOMATIONS_ERROR',
};

export const AUTOMATION_STATUS = {
    DRAFT: 0,
    ACTIVE: 1,
    PAUSED: 2,
};

export const AUTOMATION_NODE_TYPE = {
    TRIGGER: 0,
    BRANCH: 1,
    SEND_MESSAGE: 2,
    SEND_SURVEY: 3,
    WAIT: 4,
    EXIT: 99,
};

export const TRIGGER_SOURCE_TYPE = {
    CONTACT_ACTIVITY: 0,
    DATE: 1,
};

export const AUTOMATION_DATE_SOURCE = {
    BIRTHDAY: 0,
    FIXED_DATE: 1,
};

export const CONDITION_LOGIC = {
    AND: 0,
    OR: 1,
};

export const CONDITION_FIELD_SOURCE = {
    CONTACT_FIELD: 0,
    CONTACT_LABEL: 1,
    CUSTOM_EVENT_DATA: 2,
    TRIGGER_ACTIVITY: 3,
};

export const CONDITION_OPERATOR = {
    EQUALS: 0,
    NOT_EQUALS: 1,
    CONTAINS: 2,
    NOT_CONTAINS: 3,
    GREATER_THAN: 4,
    LESS_THAN: 5,
    EXISTS: 6,
    NOT_EXISTS: 7,
    IN: 8,
    NOT_IN: 9,
};

export const BRANCH_KEY = {
    YES: 'yes',
    NO: 'no',
};

export const AUTOMATION_NODE_KIND = {
    TRIGGER: 'trigger',
    BRANCH: 'branch',
    SEND_MESSAGE: 'sendMessage',
    SEND_COUPON: 'sendCoupon',
    SEND_SURVEY: 'sendSurvey',
    WAIT: 'wait',
    EXIT: 'exit',
};

const NODE_TYPE_KIND = {
    [AUTOMATION_NODE_TYPE.TRIGGER]: AUTOMATION_NODE_KIND.TRIGGER,
    [AUTOMATION_NODE_TYPE.BRANCH]: AUTOMATION_NODE_KIND.BRANCH,
    [AUTOMATION_NODE_TYPE.SEND_MESSAGE]: AUTOMATION_NODE_KIND.SEND_MESSAGE,
    [AUTOMATION_NODE_TYPE.SEND_SURVEY]: AUTOMATION_NODE_KIND.SEND_SURVEY,
    [AUTOMATION_NODE_TYPE.WAIT]: AUTOMATION_NODE_KIND.WAIT,
    [AUTOMATION_NODE_TYPE.EXIT]: AUTOMATION_NODE_KIND.EXIT,
};

export const getNodeKind = data =>
    data.type === AUTOMATION_NODE_TYPE.SEND_MESSAGE && data.isCoupon
        ? AUTOMATION_NODE_KIND.SEND_COUPON
        : NODE_TYPE_KIND[data.type];

export const AUTOMATION_TRIGGER_OPTIONS = [
    { value: CONTACT_ACTIVITY_TYPE.ContactCreated, key: 'contactCreated' },
    { value: CONTACT_ACTIVITY_TYPE.SurveyResponded, key: 'surveyResponded' },
    { value: CONTACT_ACTIVITY_TYPE.SurveySent, key: 'surveySent' },
    { value: CONTACT_ACTIVITY_TYPE.ConversationClosed, key: 'conversationResolved' },
    { value: CONTACT_ACTIVITY_TYPE.ReviewLeft, key: 'reviewLeft' },
    { value: CONTACT_ACTIVITY_TYPE.Integration, key: 'integrationEvent' },
    { value: CONTACT_ACTIVITY_TYPE.CustomEvent, key: 'customEvent' },
    { value: CONTACT_ACTIVITY_TYPE.CouponRedeemed, key: 'couponRedeemed' },
    { value: CONTACT_ACTIVITY_TYPE.FormSubmitted, key: 'formSubmitted' },
    { value: CONTACT_ACTIVITY_TYPE.AddedToSegment, key: 'addedToSegment' },
];

export const SEGMENT_TRIGGER = CONTACT_ACTIVITY_TYPE.AddedToSegment;

export const CUSTOM_EVENT_TRIGGER = CONTACT_ACTIVITY_TYPE.CustomEvent;
export const INTEGRATION_TRIGGER = CONTACT_ACTIVITY_TYPE.Integration;

// Integrations that write ContactActivityType.Integration rows.
export const AUTOMATION_INTEGRATION_TRIGGERS = [
    { integration: AccIntegrationName.CoverManager, key: 'integrationCoverManager' },
    { integration: AccIntegrationName.VTEX, key: 'integrationVtex' },
];

export const EVENT_FIELD_INPUT = {
    SELECT: 'select',
    INTEGER: 'integer',
    DECIMAL: 'decimal',
    TEXT: 'text',
    BOOLEAN: 'boolean',
};

export const EVENT_FIELD_OPTIONS = {
    PROVIDERS: 'providers',
    ACCOUNTS: 'accounts',
    LOCATIONS: 'locations',
    BRANDS: 'brands',
    SEXES: 'sexes',
    LANGUAGES: 'languages',
};

export const PAYMENT_PROVIDERS = [{ value: 'square', label: 'Square', integration: AccIntegrationName.Square }];

const PAYMENT_EVENT_FIELDS = [
    { path: 'provider', key: 'provider', input: EVENT_FIELD_INPUT.SELECT, options: EVENT_FIELD_OPTIONS.PROVIDERS },
    {
        path: 'externalAccountId',
        key: 'account',
        input: EVENT_FIELD_INPUT.SELECT,
        options: EVENT_FIELD_OPTIONS.ACCOUNTS,
    },
    { path: 'locationId', key: 'location', input: EVENT_FIELD_INPUT.SELECT, options: EVENT_FIELD_OPTIONS.LOCATIONS },
    { path: 'amount', key: 'amount', input: EVENT_FIELD_INPUT.DECIMAL },
    { path: 'currency', key: 'currency', input: EVENT_FIELD_INPUT.TEXT },
    { path: 'firstPaymentForContact', key: 'firstPurchase', input: EVENT_FIELD_INPUT.BOOLEAN },
    { path: 'paymentCount', key: 'paymentCount', input: EVENT_FIELD_INPUT.INTEGER },
];

const BOOKING_EVENT_FIELDS = [
    { path: 'locationId', key: 'location', input: EVENT_FIELD_INPUT.SELECT, options: EVENT_FIELD_OPTIONS.LOCATIONS },
    { path: 'locationBrandId', key: 'brand', input: EVENT_FIELD_INPUT.SELECT, options: EVENT_FIELD_OPTIONS.BRANDS },
];

export const AUTOMATION_EVENT_TRIGGERS = [
    {
        eventType: PAYMENT_COMPLETED_EVENT,
        key: 'paymentCompleted',
        integrations: PAYMENT_PROVIDERS.map(provider => provider.integration),
        fields: PAYMENT_EVENT_FIELDS,
    },
    {
        eventType: BOOKING_STARTED_EVENT,
        key: 'bookingStarted',
        integrations: [AccIntegrationName.BookingsFeed],
        fields: BOOKING_EVENT_FIELDS,
    },
    {
        eventType: BOOKING_ENDED_EVENT,
        key: 'bookingEnded',
        integrations: [AccIntegrationName.BookingsFeed],
        fields: BOOKING_EVENT_FIELDS,
    },
];

export const findEventTrigger = eventType => AUTOMATION_EVENT_TRIGGERS.find(option => option.eventType === eventType);

const INTEGRATION_TRIGGER_PREFIX = 'int:';
const EVENT_TRIGGER_PREFIX = 'evt:';

export const integrationTriggerId = integration => `${INTEGRATION_TRIGGER_PREFIX}${integration}`;
export const eventTriggerId = eventType => `${EVENT_TRIGGER_PREFIX}${eventType}`;
export const isEventTriggerId = id => typeof id === 'string' && id.startsWith(EVENT_TRIGGER_PREFIX);

// The umbrella Integration activity type is an implementation detail — everywhere a trigger is
// shown, counted or picked it is replaced by the integrations it was narrowed to.
export const expandTriggerIds = (activityTypes = [], integrationTypes = [], customEventType = null) => {
    const eventTrigger = activityTypes.includes(CUSTOM_EVENT_TRIGGER) ? findEventTrigger(customEventType) : null;
    return [
        ...activityTypes.filter(
            value =>
                (!integrationTypes.length || value !== INTEGRATION_TRIGGER) &&
                (!eventTrigger || value !== CUSTOM_EVENT_TRIGGER),
        ),
        ...integrationTypes.map(integrationTriggerId),
        ...(eventTrigger ? [eventTriggerId(eventTrigger.eventType)] : []),
    ];
};

export const collapseTriggerIds = triggerIds => {
    const integrationTypes = triggerIds
        .filter(id => typeof id === 'string' && id.startsWith(INTEGRATION_TRIGGER_PREFIX))
        .map(id => Number(id.slice(INTEGRATION_TRIGGER_PREFIX.length)));
    const eventTypes = triggerIds.filter(isEventTriggerId).map(id => id.slice(EVENT_TRIGGER_PREFIX.length));
    const activityTypes = triggerIds.filter(id => typeof id === 'number');

    if (integrationTypes.length) {
        activityTypes.push(INTEGRATION_TRIGGER);
    }

    if (eventTypes.length && !activityTypes.includes(CUSTOM_EVENT_TRIGGER)) {
        activityTypes.push(CUSTOM_EVENT_TRIGGER);
    }

    return eventTypes.length
        ? { activityTypes, integrationTypes, customEventType: eventTypes[0] }
        : { activityTypes, integrationTypes };
};
